import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MdSearch } from 'react-icons/md';
import API from '../api';
import { useLanguage } from '../i18n';
import { useAuth } from '../hooks/useAuth';

// Search everything from the top bar. The lists are downloaded once (the first
// time the box is used) and filtered in the browser, so typing feels instant.
const asList = data => (Array.isArray(data) ? data : (data && (data.items || data.data || data.results)) || []);
const STALE_MS = 2 * 60 * 1000;

export default function GlobalSearch() {
    const { t } = useLanguage();
    const navigate = useNavigate();
    const { user, isEmployee } = useAuth();
    const [text, setText] = useState('');
    const [open, setOpen] = useState(false);
    const [active, setActive] = useState(0);
    const [rows, setRows] = useState([]);
    const loadedAt = useRef(0);
    const loading = useRef(false);
    const inputRef = useRef(null);
    const boxRef = useRef(null);

    const load = useCallback(async () => {
        if (loading.current || Date.now() - loadedAt.current < STALE_MS) return;
        loading.current = true;
        const calls = isEmployee
            ? [['person', '/auth/users/list'], ['request', '/asset-requests']]
            : [['asset', '/assets'], ['workOrder', '/work-orders'], ['space', '/spaces'], ['item', '/inventory'], ['person', '/auth/users/list'], ['request', '/asset-requests']];
        const results = await Promise.allSettled(calls.map(([, url]) => API.get(url)));
        const out = [];
        results.forEach((result, i) => {
            if (result.status !== 'fulfilled') return;
            const kind = calls[i][0];
            asList(result.value.data).forEach(row => {
                if (!row) return;
                if (kind === 'asset') out.push({ kind, id: row.id, title: row.name, sub: row.asset_tag, to: '/app/assets' });
                else if (kind === 'workOrder') out.push({ kind, id: row.id, title: row.title, sub: row.wo_number, to: '/app/work-orders' });
                else if (kind === 'space') out.push({ kind, id: row.id, title: row.name, sub: row.room_number || row.floor, to: '/app/spaces' });
                else if (kind === 'item') out.push({ kind, id: row.id, title: row.name, sub: row.item_code, to: '/app/inventory' });
                else if (kind === 'person' && row.id !== user.id && row.role !== 'developer') out.push({ kind, id: row.id, title: row.name, sub: row.job_title || row.role, to: `/app/messages?chat=${row.id}` });
                else if (kind === 'request') out.push({ kind, id: row.id, title: row.asset_name, sub: row.requester_name, to: `/app/requests?id=${row.id}` });
            });
        });
        setRows(out.filter(r => r.title));
        loadedAt.current = Date.now();
        loading.current = false;
    }, [isEmployee, user.id]);

    useEffect(() => {
        const onDown = event => { if (boxRef.current && !boxRef.current.contains(event.target)) setOpen(false); };
        const onKey = event => {
            const tag = (event.target && event.target.tagName) || '';
            if (event.key === '/' && !['INPUT', 'TEXTAREA', 'SELECT'].includes(tag) && !event.target.isContentEditable) {
                event.preventDefault();
                inputRef.current && inputRef.current.focus();
            }
        };
        document.addEventListener('mousedown', onDown);
        document.addEventListener('keydown', onKey);
        return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
    }, []);

    const needle = text.trim().toLowerCase();
    const matches = useMemo(() => {
        if (needle.length < 2) return [];
        return rows.filter(r => `${r.title} ${r.sub || ''}`.toLowerCase().includes(needle)).slice(0, 8);
    }, [rows, needle]);

    const choose = row => {
        setOpen(false);
        setText('');
        navigate(row.to);
    };

    const onKeyDown = event => {
        if (event.key === 'ArrowDown') { event.preventDefault(); setActive(i => Math.min(i + 1, matches.length - 1)); }
        else if (event.key === 'ArrowUp') { event.preventDefault(); setActive(i => Math.max(i - 1, 0)); }
        else if (event.key === 'Enter' && matches[active]) { event.preventDefault(); choose(matches[active]); }
        else if (event.key === 'Escape') { setOpen(false); inputRef.current && inputRef.current.blur(); }
    };

    const typeLabel = kind => t(`search.type${kind.charAt(0).toUpperCase()}${kind.slice(1)}`);

    return (
        <div ref={boxRef} className="d-none d-md-block" style={{ position: 'relative', marginRight: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, border: '1px solid #e2e8f0', borderRadius: 10, padding: '0 10px', height: 36, background: '#f8fafc', width: 'clamp(150px, 13vw, 240px)' }}>
                <MdSearch size={18} color="#94a3b8" />
                <input ref={inputRef} value={text} placeholder={t('search.placeholder')} aria-label={t('search.placeholder')}
                    style={{ flex: 1, border: 0, outline: 0, background: 'transparent', fontSize: 13, minWidth: 0 }}
                    onFocus={() => { setOpen(true); load(); }}
                    onChange={event => { setText(event.target.value); setActive(0); setOpen(true); }}
                    onKeyDown={onKeyDown} />
            </div>
            {open && text.trim() && (
                <div style={{ position: 'absolute', right: 0, top: 'calc(100% + 6px)', width: 340, maxWidth: '90vw', background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, boxShadow: '0 16px 36px rgba(15,23,42,.18)', zIndex: 3000, overflow: 'hidden' }}>
                    {needle.length < 2 && <div style={{ padding: '12px 14px', color: '#94a3b8', fontSize: 13 }}>{t('search.typeMore')}</div>}
                    {needle.length >= 2 && !matches.length && <div style={{ padding: '12px 14px', color: '#94a3b8', fontSize: 13 }}>{t('search.noResults')}</div>}
                    {matches.map((row, index) => (
                        <button type="button" key={`${row.kind}-${row.id}`} onMouseDown={event => event.preventDefault()} onClick={() => choose(row)} onMouseEnter={() => setActive(index)}
                            style={{ display: 'flex', width: '100%', alignItems: 'center', gap: 10, textAlign: 'left', padding: '9px 14px', border: 0, background: index === active ? '#eff6ff' : '#fff', cursor: 'pointer' }}>
                            <span style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.5px', color: '#0369a1', background: '#e0f2fe', borderRadius: 6, padding: '2px 6px', flexShrink: 0 }}>{typeLabel(row.kind)}</span>
                            <span style={{ flex: 1, minWidth: 0 }}>
                                <span style={{ display: 'block', fontSize: 13.5, color: '#0f172a', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{row.title}</span>
                                {row.sub && <span style={{ display: 'block', fontSize: 11.5, color: '#94a3b8' }}>{row.sub}</span>}
                            </span>
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}
